import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import {
  Shield, ShieldCheck, Activity, Loader, AlertTriangle, RefreshCw,
  ChevronDown, ChevronRight, CheckCircle, XCircle, Clock, Hash, Eye, Lock,
  Filter, ExternalLink, Layers, FileText, Database,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import WorkflowEnforcementAuditPanel from '../components/workflow/WorkflowEnforcementAuditPanel';

/**
 * WorkflowAuditPage — unified audit center spanning both workflow archetypes.
 *
 * <h3>Visual language</h3>
 * <p>Matches the Policy Config / Workflow Discovery treatment: stone palette,
 * red/coral brand accent, full-width underline tabs, accent-top stat cards,
 * subtle gradient description strip below the tab nav, and consistent
 * `pcp-*` hover classes so the entire Governance section reads as one
 * platform surface.</p>
 *
 * <h3>Data model</h3>
 * <p>Calls {@code GET /api/v1/audit/runs} (see WorkflowAuditController).
 * Each row is one workflow run regardless of archetype, plus a per-stage
 * rollup ({@code stages[]}) so the row strip can show which of the 7
 * canonical stages fired (PRE_GATE → DPG_SCAN → POLICY_EVAL → DECISION →
 * EXECUTION → POST_AUDIT → POST_RECORD).</p>
 *
 * <p>Native runs use {@code policy_audit_events} (event count). External
 * GEL Protected runs use {@code governance_audit_envelopes} (envelope
 * count). Both stream into the same uniform shape.</p>
 */

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — mirrored from PolicyConfigPage so the two pages share a
   visual lineage. Never edit these in isolation; the Governance section's
   four pages are deliberately kinetic-similar.
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)',
  mid: '#d4453a', soft: '#f8d7d4', muted: '#eab3ad', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald:  { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:    { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:      { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:     { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:   { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:    { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

/* ─── Tab definitions (source-archetype filter) ─────────────────────────── */
const SOURCE_TABS = [
  { key: 'ALL',      label: 'All Workflows',  icon: Layers,   desc: 'Every audit-recorded run across native and external archetypes.' },
  { key: 'EXTERNAL', label: 'External (GEL Protected)', icon: ShieldCheck, desc: 'Runs of discovered workflows protected by the GEL enforcement chain — hash-chained envelopes per phase.' },
  { key: 'NATIVE',   label: 'Native (Agentlets)', icon: Activity, desc: 'Runs of Agentlets-authored workflows — policy audit events keyed by workflow_execution_id.' },
];

const DECISION_META = {
  ALLOW: { Icon: CheckCircle,   accent: ACCENT.emerald, label: 'Allow' },
  WARN:  { Icon: AlertTriangle, accent: ACCENT.amber,   label: 'Warn'  },
  BLOCK: { Icon: XCircle,       accent: ACCENT.rose,    label: 'Block' },
};

const STAGE_ORDER = ['PRE_GATE', 'DPG_SCAN', 'POLICY_EVAL', 'DECISION', 'EXECUTION', 'POST_AUDIT', 'POST_RECORD'];
const STAGE_META = {
  PRE_GATE:    { label: 'Pre-Gate',    short: 'PG', Icon: Lock,        accent: ACCENT.slate },
  DPG_SCAN:    { label: 'DPG Scan',    short: 'DPG', Icon: Eye,        accent: ACCENT.violet },
  POLICY_EVAL: { label: 'Policy Eval', short: 'PE',  Icon: Shield,     accent: ACCENT.sky    },
  DECISION:    { label: 'Decision',    short: 'DEC', Icon: CheckCircle, accent: ACCENT.emerald },
  EXECUTION:   { label: 'Execution',   short: 'EX',  Icon: Activity,   accent: ACCENT.sky    },
  POST_AUDIT:  { label: 'Audit',       short: 'AU',  Icon: FileText,   accent: ACCENT.slate  },
  POST_RECORD: { label: 'Post-Record', short: 'PR',  Icon: Database,   accent: ACCENT.slate  },
};

const WINDOW_OPTIONS = [
  { id: '24h', label: 'Last 24 hours', hours: 24 },
  { id: '7d',  label: 'Last 7 days',   hours: 24 * 7 },
  { id: '30d', label: 'Last 30 days',  hours: 24 * 30 },
  { id: '90d', label: 'Last 90 days',  hours: 24 * 90 },
];

const PAGE_SIZES = [10, 50, 100];

/* ─── Shared base styles ───────────────────────────────────────────────── */
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};

/* ─── CSS keyframes (match PolicyConfig prefix for visual consistency) ─── */
const GlobalStyles = () => (
  <style>{`
    @keyframes wapSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes wapFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes wapShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    .wap-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .wap-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; transform: translateY(-1px); }
    .wap-stat-card { transition: all 0.2s ease !important; }
    .wap-stat-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
    .wap-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .wap-row:hover { background: ${S[25]} !important; }
    .wap-btn-ghost:hover { background: ${S[100]} !important; }
    .wap-btn-primary:hover { filter: brightness(1.08); box-shadow: 0 2px 8px rgba(226,74,58,0.25); }
    .wap-pagebtn:disabled { opacity: 0.4; cursor: not-allowed; }
    .wap-pagebtn:not(:disabled):hover { background: ${S[100]}; border-color: ${S[300]}; }
  `}</style>
);

export default function WorkflowAuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Filters ─────────────────────────────────────────────────────────
  const [source, setSource] = useState(searchParams.get('source') || 'ALL');
  const [decision, setDecision] = useState(searchParams.get('decision') || null);
  /* G396 — When the page is filtered to a single workflow (the canvas
   * "Audit & Runs" link), default the window to 30d instead of 7d.
   * Otherwise runs on an older workflow won't appear unless the operator
   * widens the window manually — confusing because they just clicked the
   * Audit link from THAT workflow's canvas. */
  const initialWorkflowId = searchParams.get('workflowId') || null;
  const [windowId, setWindowId] = useState(
    searchParams.get('window') || (initialWorkflowId ? '30d' : '7d')
  );
  const [workflowFilter, setWorkflowFilter] = useState(initialWorkflowId);
  const [pageSize, setPageSize] = useState(() => {
    const fromUrl = parseInt(searchParams.get('pageSize'), 10);
    return PAGE_SIZES.includes(fromUrl) ? fromUrl : 10;
  });
  const [page, setPage] = useState(0);

  // ── Data ─────────────────────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRunId, setExpandedRunId] = useState(null);

  const fromInstant = useMemo(() => {
    const opt = WINDOW_OPTIONS.find((w) => w.id === windowId) || WINDOW_OPTIONS[1];
    return new Date(Date.now() - opt.hours * 60 * 60 * 1000).toISOString();
  }, [windowId]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always pull a generous set from the backend (max 500); pagination is
      // client-side over the resulting list. Keeps the query path simple and
      // consistent with how the Workflow Discovery Audit table works.
      const res = await api.getAuditRuns({
        source: source === 'ALL' ? null : source.toLowerCase(),
        decision: decision || null,
        workflowId: workflowFilter || null,
        from: fromInstant,
        limit: 500,
      });
      if (res.ok) setData(res.data);
      else setError(res.data?.message || 'Failed to load');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [source, decision, workflowFilter, fromInstant]);

  useEffect(() => { reload(); }, [reload]);

  // Reset to first page whenever filters change so the user doesn't end up
  // on an empty trailing page after toggling source/decision/window.
  useEffect(() => { setPage(0); }, [source, decision, windowId, workflowFilter, pageSize]);

  // Reflect filters in URL so deep-links (e.g. from canvas Audit & Runs) are stable.
  useEffect(() => {
    const next = new URLSearchParams();
    if (source && source !== 'ALL') next.set('source', source);
    if (decision) next.set('decision', decision);
    if (windowId) next.set('window', windowId);
    if (workflowFilter) next.set('workflowId', workflowFilter);
    if (pageSize && pageSize !== 10) next.set('pageSize', String(pageSize));
    setSearchParams(next, { replace: true });
  }, [source, decision, windowId, workflowFilter, pageSize, setSearchParams]);

  const rows = data?.rows || [];
  const totals = data?.totals || { allow: 0, warn: 0, block: 0, native: 0, external: 0 };

  // Group runs by workflow (one card per workflow, runs underneath).
  const groups = useMemo(() => {
    const byWorkflow = new Map();
    for (const r of rows) {
      const key = r.workflowName || r.externalWorkflowId || String(r.workflowId || '');
      if (!byWorkflow.has(key)) {
        byWorkflow.set(key, {
          workflowName: r.workflowName || '(unnamed)',
          workflowId: r.workflowId,
          sources: new Set(),
          runs: [],
        });
      }
      const g = byWorkflow.get(key);
      g.sources.add(r.source);
      g.runs.push(r);
    }
    return Array.from(byWorkflow.values()).sort((a, b) => {
      const aTime = a.runs[0]?.startedAt || '';
      const bTime = b.runs[0]?.startedAt || '';
      return aTime < bTime ? 1 : aTime > bTime ? -1 : 0;
    });
  }, [rows]);

  // Client-side pagination over the flat row list. Group cards still render
  // their full run set when their first row falls into the current page —
  // pagination is applied to ROWS, not groups, to match the discovery audit's
  // table-style pagination model.
  const totalRows = rows.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const pagedRowKeys = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return new Set(rows.slice(start, end).map((r) => `${r.source}:${r.runId}`));
  }, [rows, page, pageSize]);

  const activeTabMeta = SOURCE_TABS.find((t) => t.key === source) || SOURCE_TABS[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', background: S[50] }}>
      <GlobalStyles />

      {/* ═══ HEADER ═══════════════════════════════════════════════════════ */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${S[200]}`, flexShrink: 0 }}>
        <div style={{ padding: '22px 28px' }}>
          <PageHeader
            icon={Shield}
            title="Workflow Audit"
            subtitle="Unified compliance trail across native and external (GEL Protected) workflow runs"
            actions={
              <button className="wap-btn-ghost" onClick={reload} disabled={loading} style={{
                ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
                background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
              }}>
                <RefreshCw size={13} style={{ animation: loading ? 'wapSpin 1s linear infinite' : 'none' }} />
                Refresh
              </button>
            }
          />
        </div>

        {/* ─── Source-archetype tabs ─── */}
        <div style={{ display: 'flex', gap: 0, marginTop: 16, padding: '0 20px' }}>
          {SOURCE_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = source === tab.key;
            const count = tab.key === 'NATIVE' ? totals.native
                        : tab.key === 'EXTERNAL' ? totals.external
                        : totalRows;
            return (
              <button key={tab.key}
                className={isActive ? '' : 'wap-tab'}
                onClick={() => setSource(tab.key)}
                title={tab.desc}
                style={{
                  flex: 1, padding: '14px 16px',
                  borderBottom: isActive ? `3px solid ${BRAND.primary}` : '3px solid transparent',
                  background: 'transparent', border: 'none', borderBottomStyle: 'solid',
                  cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 500,
                  color: isActive ? BRAND.primary : S[500],
                  transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center',
                  whiteSpace: 'nowrap',
                }}>
                <Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} />
                {tab.label}
                <span style={{
                  fontSize: 10, fontWeight: 600,
                  background: isActive ? BRAND.light : S[100],
                  color: isActive ? BRAND.primary : S[400],
                  padding: '1px 7px', borderRadius: 8, marginLeft: 2,
                }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Active-tab description bar ─── */}
      <div style={{
        padding: '8px 24px', background: BRAND.gradientSubtle,
        borderBottom: `1px solid ${S[150]}`,
        fontSize: 11, color: S[500], fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
      }}>
        <activeTabMeta.icon size={12} color={S[400]} />
        {activeTabMeta.desc}
      </div>

      {/* ═══ MAIN BODY ═════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 32px' }}>

        {/* ─── Stat cards row ─── */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard label="External Runs" value={totals.external} accent={ACCENT.sky}     icon={ShieldCheck}    loading={loading} />
          <StatCard label="Native Runs"   value={totals.native}   accent={ACCENT.violet}  icon={Activity}       loading={loading} />
          <StatCard label="Allow"         value={totals.allow}    accent={ACCENT.emerald} icon={CheckCircle}    loading={loading} />
          <StatCard label="Warn"          value={totals.warn}     accent={ACCENT.amber}   icon={AlertTriangle}  loading={loading} />
          <StatCard label="Block"         value={totals.block}    accent={ACCENT.rose}    icon={XCircle}        loading={loading} />
        </div>

        {/* ─── Filter strip ─── */}
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          padding: '12px 14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          gap: 14, marginBottom: 16,
        }}>
          {/* Decision pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: S[500],
              textTransform: 'uppercase', letterSpacing: '0.06em' }}>Decision</span>
            {[null, 'ALLOW', 'WARN', 'BLOCK'].map((d) => {
              const active = decision === d;
              const meta = d ? DECISION_META[d] : null;
              return (
                <button key={d || 'all'} onClick={() => setDecision(d)}
                  style={{
                    ...btnBase, padding: '5px 11px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.04em', cursor: 'pointer', fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
                    border: active
                      ? `1px solid ${meta ? meta.accent.icon : S[700]}`
                      : `1px solid ${S[200]}`,
                    background: active ? (meta ? meta.accent.bg : S[900]) : '#fff',
                    color: active ? (meta ? meta.accent.text : '#fff') : S[600],
                  }}>
                  {d || 'ALL'}
                </button>
              );
            })}
          </div>

          <div style={{ width: 1, height: 24, background: S[200] }} />

          {/* Time window */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: S[500],
              textTransform: 'uppercase', letterSpacing: '0.06em' }}>Window</span>
            <select value={windowId} onChange={(e) => setWindowId(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                fontSize: 11, color: S[700], cursor: 'pointer', background: '#fff',
                fontFamily: 'inherit',
              }}>
              {WINDOW_OPTIONS.map((w) => (
                <option key={w.id} value={w.id}>{w.label}</option>
              ))}
            </select>
          </div>

          <div style={{ width: 1, height: 24, background: S[200] }} />

          {/* Page size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: S[500],
              textTransform: 'uppercase', letterSpacing: '0.06em' }}>Rows</span>
            <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              style={{
                padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                fontSize: 11, color: S[700], cursor: 'pointer', background: '#fff',
                fontFamily: 'inherit',
              }}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {workflowFilter && (
            <>
              <div style={{ width: 1, height: 24, background: S[200] }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', borderRadius: 6, background: BRAND.light,
                border: `1px solid ${BRAND.soft}`, fontSize: 11, color: BRAND.dark }}>
                <Filter size={11} />
                Workflow filter:
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>
                  {String(workflowFilter).slice(0, 8)}…
                </span>
                <button onClick={() => setWorkflowFilter(null)}
                  style={{ ...btnBase, background: 'none', cursor: 'pointer',
                    color: BRAND.dark, fontWeight: 700, padding: 0, marginLeft: 4 }}>
                  ×
                </button>
              </div>
            </>
          )}

          {/* Range indicator on the right */}
          <div style={{ marginLeft: 'auto', fontSize: 11, color: S[500] }}>
            {totalRows === 0 ? '0 of 0'
              : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, totalRows)} of ${totalRows}`}
          </div>
        </div>

        {/* ─── Body states ─── */}
        {loading && <BodyLoading />}
        {!loading && error && <BodyError message={error} />}
        {!loading && !error && rows.length === 0 && <BodyEmpty />}

        {!loading && !error && groups.map((g, idx) => {
          // Only render rows from this group that fall inside the current
          // page window. If none do, skip the group entirely.
          const visibleRuns = g.runs.filter((r) => pagedRowKeys.has(`${r.source}:${r.runId}`));
          if (visibleRuns.length === 0) return null;
          return (
            <WorkflowGroupCard
              key={`${g.workflowName}-${idx}`}
              group={{ ...g, runs: visibleRuns }}
              expandedRunId={expandedRunId}
              onToggleRun={(runId) => setExpandedRunId(expandedRunId === runId ? null : runId)}
              onSelectWorkflow={(wid) => setWorkflowFilter(wid)}
              onOpenInCanvas={(wid) => navigate(`/canvas?workflowId=${wid}`)}
            />
          );
        })}

        {/* ─── Pagination footer ─── */}
        {!loading && !error && totalRows > pageSize && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 16px', background: '#fff',
            border: `1px solid ${S[200]}`, borderRadius: 12, marginTop: 8,
          }}>
            <span style={{ fontSize: 11, color: S[500] }}>
              Page <strong style={{ color: S[700], fontWeight: 700 }}>{page + 1}</strong> of {pageCount}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="wap-pagebtn" onClick={() => setPage(0)} disabled={page === 0}
                style={pageBtnStyle()}>« First</button>
              <button className="wap-pagebtn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                style={pageBtnStyle()}>‹ Prev</button>
              <button className="wap-pagebtn" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                style={pageBtnStyle()}>Next ›</button>
              <button className="wap-pagebtn" onClick={() => setPage(pageCount - 1)} disabled={page >= pageCount - 1}
                style={pageBtnStyle()}>Last »</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   STAT CARD — accent-top-line variant (PolicyConfig style)
   ══════════════════════════════════════════════════════════════════════════════ */

function StatCard({ label, value, accent, icon: Icon, loading: isLoading }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="wap-stat-card" style={{
      flex: 1, minWidth: 180, background: '#fff', border: `1px solid ${S[200]}`,
      borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 8,
          }}>{label}</div>
          {isLoading ? (
            <div style={{
              width: 48, height: 28, borderRadius: 6,
              background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`,
              backgroundSize: '200% 100%', animation: 'wapShimmer 1.5s infinite',
            }} />
          ) : (
            <div style={{
              fontSize: 30, fontWeight: 800, color: S[900],
              lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'",
            }}>{value}</div>
          )}
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: a.bg,
          border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {Icon && <Icon size={17} color={a.icon} strokeWidth={2} />}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   STAGE STRIP — answer to the user's "show enforcement stages" feedback.
   Renders 7 small pips, each colored by whether that stage fired during
   the run (and what its worst decision was). Hidden gracefully when the
   backend hasn't supplied a stages array (legacy rows).
   ══════════════════════════════════════════════════════════════════════════════ */

function StageStrip({ stages }) {
  if (!Array.isArray(stages) || stages.length === 0) {
    return <span style={{ width: 168, fontSize: 9, color: S[300] }}>—</span>;
  }
  const ordered = STAGE_ORDER.map((key) => stages.find((s) => s.stage === key) || { stage: key, fired: false });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 168 }}>
      {ordered.map((s, i) => {
        const meta = STAGE_META[s.stage] || STAGE_META.EXECUTION;
        const dm = s.fired
          ? (DECISION_META[(s.decision || 'ALLOW').toUpperCase()] || DECISION_META.ALLOW).accent
          : null;
        const bg = s.fired ? dm.bg : S[100];
        const border = s.fired ? dm.border : S[200];
        const color = s.fired ? dm.text : S[400];
        return (
          <span key={s.stage}
            title={`${meta.label}${s.fired ? ` · ${s.decision || 'ALLOW'} · ${s.count} event${s.count !== 1 ? 's' : ''}` : ' · skipped'}`}
            style={{
              fontSize: 8, fontWeight: 700, padding: '2px 5px', borderRadius: 3,
              background: bg, border: `1px solid ${border}`, color, letterSpacing: '0.04em',
              fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
              opacity: s.fired ? 1 : 0.6,
            }}>
            {meta.short}
          </span>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   WORKFLOW GROUP CARD — header (workflow name + source badge + actions) over
   a stack of run rows.
   ══════════════════════════════════════════════════════════════════════════════ */

function WorkflowGroupCard({ group, expandedRunId, onToggleRun, onSelectWorkflow, onOpenInCanvas }) {
  const sources = Array.from(group.sources);
  const isMixed = sources.length > 1;
  const primarySource = isMixed ? 'MIXED' : sources[0];
  const sourceMeta = primarySource === 'MIXED'
    ? { label: 'Mixed',    accent: ACCENT.slate,   Icon: Layers      }
    : primarySource === 'EXTERNAL'
      ? { label: 'External', accent: ACCENT.sky,    Icon: ShieldCheck }
      : { label: 'Native',   accent: ACCENT.violet, Icon: Activity    };

  return (
    <div className="wap-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      marginBottom: 14, overflow: 'hidden',
    }}>
      {/* Workflow header */}
      <div style={{
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: `1px solid ${S[100]}`, background: S[25],
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: sourceMeta.accent.bg,
          border: `1px solid ${sourceMeta.accent.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <sourceMeta.Icon size={15} color={sourceMeta.accent.icon} strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: S[900],
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            letterSpacing: '-0.005em' }}>
            {group.workflowName}
          </div>
          <div style={{ fontSize: 11, color: S[500], marginTop: 2,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{group.runs.length} run{group.runs.length !== 1 ? 's' : ''}</span>
            <span style={{ color: S[300] }}>·</span>
            <span>{sourceMeta.label}</span>
            {group.workflowId && (
              <>
                <span style={{ color: S[300] }}>·</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: S[600] }}>
                  {String(group.workflowId).slice(0, 8)}
                </span>
              </>
            )}
          </div>
        </div>
        {group.workflowId && (
          <button onClick={() => onSelectWorkflow(group.workflowId)}
            className="wap-btn-ghost"
            title="Filter the audit list to this workflow only"
            style={{
              ...btnBase, padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
              border: `1px solid ${S[200]}`, background: '#fff', color: S[600],
            }}>
            <Filter size={11} /> Filter
          </button>
        )}
        {group.workflowId && (
          <button onClick={() => onOpenInCanvas(group.workflowId)}
            title="Open this workflow in the Canvas (DAG view)"
            style={{
              ...btnBase, padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
              border: `1px solid ${BRAND.soft}`, background: BRAND.light, color: BRAND.dark,
            }}>
            Open in Canvas <ExternalLink size={11} />
          </button>
        )}
      </div>

      {/* Run rows */}
      {group.runs.map((run) => (
        <RunRow key={`${run.source}-${run.runId}`}
          run={run}
          expanded={expandedRunId === run.runId}
          onToggle={() => onToggleRun(run.runId)}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   RUN ROW — decision badge, source badge, runId, stage strip, time, duration,
   counts, integrity dot. Click to toggle the inline 7-stage timeline.
   ══════════════════════════════════════════════════════════════════════════════ */

function RunRow({ run, expanded, onToggle }) {
  const decision = (run.decision || 'ALLOW').toUpperCase();
  const dm = DECISION_META[decision] || DECISION_META.ALLOW;
  const isNative = run.source === 'NATIVE';
  // Both archetypes now drive the same timeline component. The panel
  // routes to the native or external endpoint based on the run's source
  // flag, which we propagate down through the runs prop below.
  const showTimeline = expanded && run.runId;
  const startedAt = run.startedAt ? new Date(run.startedAt) : null;
  const verifyDot = run.integrityVerified === true ? ACCENT.emerald
                  : run.integrityVerified === false ? ACCENT.rose
                  : ACCENT.slate;

  return (
    <div>
      <button onClick={onToggle}
        className="wap-row"
        style={{
          width: '100%', padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10,
          background: expanded ? S[25] : '#fff', border: 'none',
          borderBottom: `1px solid ${S[100]}`, cursor: 'pointer',
          textAlign: 'left', fontFamily: 'inherit', transition: 'background 0.12s ease',
        }}>
        {expanded ? <ChevronDown size={13} color={S[500]} />
                  : <ChevronRight size={13} color={S[500]} />}
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
          background: dm.accent.bg, border: `1px solid ${dm.accent.border}`,
          color: dm.accent.text, letterSpacing: '0.04em',
          fontFamily: "'IBM Plex Mono', monospace",
        }}>
          {decision}
        </span>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
          background: isNative ? ACCENT.violet.bg : ACCENT.sky.bg,
          border: `1px solid ${isNative ? ACCENT.violet.border : ACCENT.sky.border}`,
          color: isNative ? ACCENT.violet.text : ACCENT.sky.text,
          letterSpacing: '0.04em',
          fontFamily: "'IBM Plex Mono', monospace",
        }}>
          {run.source}
        </span>
        <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
          color: S[600], minWidth: 80 }}>
          {String(run.runId).slice(0, 8)}
        </span>
        <StageStrip stages={run.stages} />
        <span style={{ flex: 1, fontSize: 11, color: S[600] }}>
          {startedAt ? startedAt.toLocaleString() : '—'}
        </span>
        <span style={{ fontSize: 10, color: S[500], fontFeatureSettings: "'tnum'" }}>
          <Clock size={10} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
          {run.durationMs ? `${run.durationMs} ms` : '—'}
        </span>
        <span style={{ fontSize: 10, color: S[500], fontFeatureSettings: "'tnum'" }}>
          {run.eventCount > 0 && (<><Hash size={10} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />{run.eventCount} ev</>)}
          {run.envelopeCount > 0 && (<><Lock size={10} style={{ display: 'inline', verticalAlign: '-1px', marginLeft: 6, marginRight: 3 }} />{run.envelopeCount} env</>)}
        </span>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: verifyDot.icon, flexShrink: 0,
        }} title={
          run.integrityVerified === true ? 'Hash chain verified'
          : run.integrityVerified === false ? 'Hash chain failed'
          : 'No chain to verify'
        } />
      </button>

      {showTimeline && (
        <div style={{ padding: '10px 16px 18px', background: S[25] }}>
          <WorkflowEnforcementAuditPanel
            workflowId={run.workflowId}
            runs={[{
              id: run.runId,
              status: run.status,
              createdAt: run.startedAt,
              startedAt: run.startedAt,
              // Source flag drives the panel's endpoint routing —
              // native runs hit /workflows/runs/{uuid}/enforcement-chain;
              // external runs hit /workflows/external-runs/enforcement-chain
              // ?externalRunId=… and have their EXECUTION stage filled
              // from the inline-tasks list on GEL envelopes instead of
              // from workflow_node_executions (which they don't have).
              source: run.source,
            }]}
          />
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   BODY STATES — extracted for readability and to keep the main component focused
   on filter/state plumbing.
   ══════════════════════════════════════════════════════════════════════════════ */

function BodyLoading() {
  return (
    <div style={{ padding: 60, textAlign: 'center', color: S[500] }}>
      <Loader size={26} style={{ animation: 'wapSpin 1s linear infinite' }} />
      <div style={{ fontSize: 13, marginTop: 12 }}>Loading audit trail…</div>
    </div>
  );
}

function BodyError({ message }) {
  return (
    <div style={{
      padding: '14px 16px', background: ACCENT.rose.bg,
      border: `1px solid ${ACCENT.rose.border}`, color: ACCENT.rose.text,
      borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <AlertTriangle size={16} color={ACCENT.rose.icon} />
      {message}
    </div>
  );
}

function BodyEmpty() {
  return (
    <div style={{
      padding: 60, textAlign: 'center', color: S[500],
      background: '#fff', border: `1px dashed ${S[200]}`, borderRadius: 12,
    }}>
      <Shield size={32} color={S[300]} />
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 12, color: S[700] }}>
        No runs in this window
      </div>
      <div style={{ fontSize: 12, marginTop: 6, maxWidth: 460, margin: '6px auto 0' }}>
        Try widening the time window, switching the source toggle, or clearing
        the decision filter. Runs appear here as workflows execute and emit
        policy / DPG / protection audit records.
      </div>
    </div>
  );
}

/* ── Pagination button shared style ─────────────────────────────────────── */
function pageBtnStyle() {
  return {
    ...btnBase,
    padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    border: `1px solid ${S[200]}`, background: '#fff', color: S[700],
    cursor: 'pointer',
  };
}
